import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartFunnelOnBehalfFieldBlockConfig } from '@front-platform/content-builder';
import { kioskCartFunnelOnBehalfFieldBlockResolveConfig } from '@front-platform/content-builder';
import { Component, OnInit, input } from '@angular/core';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { OnBehalfType } from '@config/keys/on-behalf-type.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodCartFunnelValueTarget } from '@store/cart-funnel';
import { take } from 'rxjs';
export interface KioskCartFunnelOnBehalfFieldBlockRuntimeConfig {
  'block_id'?: string;
  'form_id'?: string;
}

export type KioskCartFunnelOnBehalfFieldBlockConfigInput = Partial<
  Omit<
    KioskCartFunnelOnBehalfFieldBlockConfig,
    keyof KioskCartFunnelOnBehalfFieldBlockRuntimeConfig
  > &
    KioskCartFunnelOnBehalfFieldBlockRuntimeConfig
>;
export type KioskCartFunnelOnBehalfFieldBlockResolvedConfig = Omit<
  KioskCartFunnelOnBehalfFieldBlockConfig,
  keyof KioskCartFunnelOnBehalfFieldBlockRuntimeConfig
> &
  Partial<KioskCartFunnelOnBehalfFieldBlockRuntimeConfig>;
export interface KioskCartFunnelOnBehalfFieldBlockContract extends ConfigComponentContract<
  KioskCartFunnelOnBehalfFieldBlockConfigInput,
  KioskCartFunnelOnBehalfFieldBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-funnel-on-behalf-field-block',
  templateUrl: './kiosk-cart-funnel-on-behalf-field-block.component.html',
  standalone: false,
})
export class KioskCartFunnelOnBehalfFieldBlockComponent
  implements OnInit, KioskCartFunnelOnBehalfFieldBlockContract
{
  readonly config = input<KioskCartFunnelOnBehalfFieldBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartFunnelOnBehalfFieldBlockConfig,
    KioskCartFunnelOnBehalfFieldBlockRuntimeConfig
  >(kioskCartFunnelOnBehalfFieldBlockResolveConfig, () => ({ config: this.config() }));
  private _onBehalfInputTypeConfigOverride: OnBehalfType | undefined;
  get onBehalfInputType(): OnBehalfType {
    return (
      this._onBehalfInputTypeConfigOverride ??
      (this.resolvedConfig()['on_behalf_input_type'] as unknown as OnBehalfType)
    );
  }
  set onBehalfInputType(value: OnBehalfType) {
    this._onBehalfInputTypeConfigOverride = value;
  }
  get title(): string {
    return this.resolvedConfig()['title'] as unknown as string;
  }
  get placeholder(): string | undefined {
    return this.resolvedConfig()['placeholder'] as unknown as string | undefined;
  }
  get required(): boolean {
    return this.resolvedConfig()['required'] as unknown as boolean;
  }
  get blockId(): string {
    return this.resolvedConfig()['block_id'] as unknown as string;
  }
  get formId(): string {
    return this.resolvedConfig()['form_id'] as unknown as string;
  }
  get textColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined;
  }
  get color(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['color'] as unknown as IContentBuilderFieldColor | undefined;
  }

  value: string | boolean = '';

  private readonly cartFunnelTargetValueMapping = {
    [OnBehalfType.Phone]: DoodCartFunnelValueTarget.OnBehalfUserPhone,
    [OnBehalfType.Fullname]: DoodCartFunnelValueTarget.OnBehalfUserFullName,
    [OnBehalfType.Email]: DoodCartFunnelValueTarget.OnBehalfUserEmail,
    [OnBehalfType.Optin]: DoodCartFunnelValueTarget.OnBehalfUserOptin,
  };

  constructor(
    private cartFunnelSelector: CartFunnelStoreSelector,
    private cartFunnelDispatcher: CartFunnelStoreDispatcher,
  ) {}

  ngOnInit(): void {
    this.cartFunnelDispatcher.addValue({
      id: this.blockId,
      title: this.title,
      displayTitle: true,
      formId: this.formId,
      required: this.required,
      target: this.getTargetForOnBehalfType(),
    });

    this.cartFunnelSelector
      .selectValue(this.blockId)
      .pipe(take(1))
      .subscribe(form => {
        this.value =
          this.onBehalfInputType === OnBehalfType.Optin
            ? Boolean(form?.value)
            : (form?.value?.toString() ?? '');
      });
  }

  private getTargetForOnBehalfType(): DoodCartFunnelValueTarget {
    return this.cartFunnelTargetValueMapping[this.onBehalfInputType];
  }

  onValueChange(_value: string | boolean): void {
    this.cartFunnelDispatcher.updateValue({
      id: this.blockId,
      changes: {
        value: _value,
      },
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartFunnelOnBehalfFieldBlockComponent, {
  'keyAliases': {},
});
